import { useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router';
import {
  createNews,
  deleteNewsMedia,
  getApiErrorMessage,
  getNews,
  importNewsFromJson,
  importNewsIntoExistingFromJson,
  listAllNewsMedia,
  listNewsCategories,
  listNewsTags,
  patchNews,
  publishNews,
  setNewsCover,
  uploadNewsMedia,
  type NewsStatus,
} from '../lib/api';
import { CONTENT_BLOCK_MEDIA_TITLE_PREFIX, isContentBlockMediaTitle, stringifyContentBlocks } from '../lib/contentBlocks';
import { createRichContentEditor } from '../lib/richContentEditor';
import { NEWS_CREATE_TEMPLATE } from '../templates/newsCreateTemplate';

const RU_TO_LAT: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'e',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'y',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'h',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'sch',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya',
};

const slugify = (value: string): string =>
  Array.from(value.toLowerCase())
    .map((char) => RU_TO_LAT[char] ?? char)
    .join('')
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

export default function CreateNewsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const template = useMemo(() => NEWS_CREATE_TEMPLATE, []);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const alert = root.querySelector<HTMLElement>('#news-form-alert');
    const form = root.querySelector<HTMLFormElement>('#news-form');
    const breadcrumb = root.querySelector<HTMLElement>('#news-form-breadcrumb');
    const formTitle = root.querySelector<HTMLElement>('#news-form-title');
    const modeBadge = root.querySelector<HTMLElement>('#news-form-mode-badge');

    const title = root.querySelector<HTMLInputElement>('#news-title');
    const slug = root.querySelector<HTMLInputElement>('#news-slug');
    const category = root.querySelector<HTMLSelectElement>('#news-category');
    const excerpt = root.querySelector<HTMLTextAreaElement>('#news-excerpt');
    const description = root.querySelector<HTMLTextAreaElement>('#news-description');
    const content = root.querySelector<HTMLTextAreaElement>('#news-content');
    const contentBlocks = root.querySelector<HTMLTextAreaElement>('#news-content-blocks');
    const contentBlocksEditorRoot = root.querySelector<HTMLElement>('#news-content-blocks-editor');
    const tags = root.querySelector<HTMLSelectElement>('#news-tags');
    const externalUrl = root.querySelector<HTMLInputElement>('#news-external-url');
    const sourceName = root.querySelector<HTMLInputElement>('#news-source-name');
    const sourceUrl = root.querySelector<HTMLInputElement>('#news-source-url');
    const featured = root.querySelector<HTMLInputElement>('#news-featured');
    const pinned = root.querySelector<HTMLInputElement>('#news-pinned');
    const seoTitle = root.querySelector<HTMLInputElement>('#news-seo-title');
    const seoDescription = root.querySelector<HTMLTextAreaElement>('#news-seo-description');
    const status = root.querySelector<HTMLSelectElement>('#news-status');
    const summaryStatus = root.querySelector<HTMLElement>('#news-summary-status');
    const reviewComment = root.querySelector<HTMLTextAreaElement>('#news-review-comment');

    const coverFile = root.querySelector<HTMLInputElement>('#news-cover-file');
    const coverName = root.querySelector<HTMLElement>('#news-cover-name');
    const coverPreview = root.querySelector<HTMLElement>('#news-cover-preview');
    const galleryFiles = root.querySelector<HTMLInputElement>('#news-gallery-files');
    const galleryPreview = root.querySelector<HTMLElement>('#news-gallery-preview');

    const previewTitle = root.querySelector<HTMLElement>('#news-preview-title');
    const previewUrl = root.querySelector<HTMLElement>('#news-preview-url');
    const previewDescription = root.querySelector<HTMLElement>('#news-preview-description');

    const saveDraft = root.querySelector<HTMLButtonElement>('#news-save-draft');
    const saveChanges = root.querySelector<HTMLButtonElement>('#news-save-changes');
    const publish = root.querySelector<HTMLButtonElement>('#news-publish');
    const importJson = root.querySelector<HTMLButtonElement>('#news-import-json');
    const lastSaved = root.querySelector<HTMLElement>('#news-last-saved');

    if (
      !alert ||
      !form ||
      !breadcrumb ||
      !formTitle ||
      !modeBadge ||
      !title ||
      !slug ||
      !category ||
      !excerpt ||
      !description ||
      !content ||
      !contentBlocks ||
      !contentBlocksEditorRoot ||
      !tags ||
      !externalUrl ||
      !sourceName ||
      !sourceUrl ||
      !featured ||
      !pinned ||
      !seoTitle ||
      !seoDescription ||
      !status ||
      !summaryStatus ||
      !reviewComment ||
      !coverFile ||
      !coverName ||
      !coverPreview ||
      !galleryFiles ||
      !galleryPreview ||
      !previewTitle ||
      !previewUrl ||
      !previewDescription ||
      !saveDraft ||
      !saveChanges ||
      !publish ||
      !importJson ||
      !lastSaved
    ) {
      return;
    }

    const editIdRaw = searchParams.get('id');
    const editId = editIdRaw ? Number(editIdRaw) : NaN;
    const isEdit = Number.isFinite(editId) && editId > 0;

    let disposed = false;
    let slugTouched = false;
    let currentStatus: NewsStatus = 'draft';
    let currentNewsId: number | null = isEdit ? editId : null;
    let selectedCover: File | null = null;
    let currentCoverObjectUrl: string | null = null;
    const selectedGallery: Array<{ file: File; url: string }> = [];
    let existingGallery: Array<{ id: number; url: string; title: string }> = [];
    let availableContentTags: Array<{ id: number; name: string; slug: string }> = [];
    const contentBlocksEditor = createRichContentEditor({
      root: contentBlocksEditorRoot,
      onChange: (blocks) => {
        contentBlocks.value = stringifyContentBlocks(blocks);
      },
      getTagOptions: () => availableContentTags,
      uploadImage: async (file) => {
        if (!currentNewsId) {
          throw new Error('Сначала сохраните новость как черновик, затем загружайте изображения в блоки.');
        }
        const media = await uploadNewsMedia(currentNewsId, { file, title: `${CONTENT_BLOCK_MEDIA_TITLE_PREFIX}${file.name}` });
        return media.file_url;
      },
    });

    const showAlert = (kind: 'error' | 'success', message: string) => {
      alert.classList.remove('hidden', 'border-rose-200', 'bg-rose-50', 'text-rose-700', 'border-emerald-200', 'bg-emerald-50', 'text-emerald-700');
      if (kind === 'error') {
        alert.classList.add('border-rose-200', 'bg-rose-50', 'text-rose-700');
      } else {
        alert.classList.add('border-emerald-200', 'bg-emerald-50', 'text-emerald-700');
      }
      alert.textContent = message;
    };

    const updatePreview = () => {
      previewTitle.textContent = title.value.trim() || 'Article title preview';
      previewUrl.textContent = `motojourney.com/news/${slug.value.trim() || 'slug'}`;
      previewDescription.textContent = seoDescription.value.trim() || excerpt.value.trim() || 'Description preview';
      summaryStatus.textContent = status.value.replace('_', ' ');
    };

    const onTitleInput = () => {
      if (!slugTouched) {
        slug.value = slugify(title.value);
      }
      updatePreview();
    };

    const onSlugInput = () => {
      slugTouched = slug.value.trim().length > 0;
      updatePreview();
    };

    const renderGallery = () => {
      const existingHtml = existingGallery
        .map(
          (item) => `
            <div class="relative aspect-square rounded-lg bg-slate-50 border border-slate-200 overflow-hidden">
              <img class="w-full h-full object-cover" src="${item.url}" alt="${item.title || `media-${item.id}`}"/>
              <button type="button" data-news-media-remove="${item.id}" class="absolute top-1 right-1 size-6 inline-flex items-center justify-center rounded-full bg-white/95 border border-slate-200 text-rose-600 hover:bg-rose-50" title="Remove media">
                <span class="material-symbols-outlined text-sm">close</span>
              </button>
            </div>
          `
        )
        .join('');

      const pendingHtml = selectedGallery
        .map(
          (item, index) => `
            <div class="relative aspect-square rounded-lg bg-slate-50 border border-slate-200 overflow-hidden">
              <img class="w-full h-full object-cover" src="${item.url}" alt="${item.file.name}"/>
              <button type="button" data-news-pending-remove="${index}" class="absolute top-1 right-1 size-6 inline-flex items-center justify-center rounded-full bg-white/95 border border-slate-200 text-rose-600 hover:bg-rose-50" title="Remove file">
                <span class="material-symbols-outlined text-sm">close</span>
              </button>
            </div>
          `
        )
        .join('');

      galleryPreview.innerHTML =
        existingHtml + pendingHtml || '<div class="col-span-3 text-xs text-slate-500">No gallery images selected.</div>';
    };

    const onCoverChange = () => {
      selectedCover = coverFile.files?.[0] ?? null;
      coverName.textContent = selectedCover ? selectedCover.name : '';
      if (currentCoverObjectUrl) {
        URL.revokeObjectURL(currentCoverObjectUrl);
        currentCoverObjectUrl = null;
      }
      if (selectedCover) {
        currentCoverObjectUrl = URL.createObjectURL(selectedCover);
        coverPreview.innerHTML = `<img class="w-full rounded-lg border border-slate-200 max-h-52 object-cover" src="${currentCoverObjectUrl}" alt="Cover"/>`;
      }
    };

    const onGalleryChange = () => {
      const files = Array.from(galleryFiles.files || []);
      files.forEach((file) => selectedGallery.push({ file, url: URL.createObjectURL(file) }));
      galleryFiles.value = '';
      renderGallery();
    };

    const onGalleryClick = (event: Event) => {
      const target = event.target as HTMLElement;
      const existingButton = target.closest<HTMLButtonElement>('button[data-news-media-remove]');
      if (existingButton && isEdit) {
        const mediaId = Number(existingButton.dataset.newsMediaRemove);
        if (!Number.isFinite(mediaId) || mediaId <= 0) return;
        void (async () => {
          try {
            await deleteNewsMedia(editId, mediaId);
            existingGallery = existingGallery.filter((item) => item.id !== mediaId);
            renderGallery();
            showAlert('success', 'Media removed.');
          } catch (error) {
            showAlert('error', getApiErrorMessage(error, 'Failed to remove media.'));
          }
        })();
        return;
      }

      const pendingButton = target.closest<HTMLButtonElement>('button[data-news-pending-remove]');
      if (!pendingButton) return;
      const index = Number(pendingButton.dataset.newsPendingRemove);
      if (!Number.isFinite(index) || index < 0 || index >= selectedGallery.length) return;
      const [removed] = selectedGallery.splice(index, 1);
      if (removed) URL.revokeObjectURL(removed.url);
      renderGallery();
    };

    const setLoading = (loading: boolean) => {
      saveDraft.disabled = loading;
      saveChanges.disabled = loading;
      publish.disabled = loading;
      importJson.disabled = loading;
      saveDraft.classList.toggle('opacity-60', loading);
      saveChanges.classList.toggle('opacity-60', loading);
      publish.classList.toggle('opacity-60', loading);
      importJson.classList.toggle('opacity-60', loading);
    };

    const setImportLoading = (loading: boolean) => {
      importJson.disabled = loading;
      saveDraft.disabled = loading;
      saveChanges.disabled = loading;
      publish.disabled = loading;
      importJson.innerHTML = loading
        ? '<span class="material-symbols-outlined text-sm animate-spin">progress_activity</span> Importing...'
        : '<span class="material-symbols-outlined text-sm">upload_file</span> Import JSON';
    };

    const openJsonImportDialog = (): Promise<string | null> =>
      new Promise((resolve) => {
        const overlay = document.createElement('div');
        overlay.className = 'fixed inset-0 z-[1500] flex items-center justify-center bg-slate-950/70 p-4';
        overlay.innerHTML = `
          <div class="w-full max-w-3xl rounded-xl border border-slate-200 bg-white p-5 shadow-2xl">
            <div class="mb-3 flex items-center justify-between">
              <h3 class="text-base font-bold text-slate-900">Import News JSON</h3>
              <button data-import-close="true" class="size-8 rounded-full border border-slate-300 text-slate-500 hover:bg-slate-100" type="button">
                <span class="material-symbols-outlined text-sm">close</span>
              </button>
            </div>
            <p class="mb-3 text-xs text-slate-500">Paste full JSON payload for news import.</p>
            <textarea data-import-json="true" class="h-72 w-full rounded-lg border border-slate-300 bg-slate-50 p-3 font-mono text-xs focus:border-primary focus:ring-primary" placeholder='{"title":"...","category":...}'></textarea>
            <div class="mt-4 flex justify-end gap-2">
              <button data-import-cancel="true" class="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100" type="button">Cancel</button>
              <button data-import-confirm="true" class="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-blue-600" type="button">Import</button>
            </div>
          </div>
        `;
        document.body.append(overlay);
        document.body.classList.add('overflow-hidden');

        const textarea = overlay.querySelector<HTMLTextAreaElement>('textarea[data-import-json="true"]');
        const close = () => {
          overlay.remove();
          document.body.classList.remove('overflow-hidden');
        };

        const onClick = (event: Event) => {
          const target = event.target as HTMLElement;
          if (target === overlay || target.closest('[data-import-close="true"]') || target.closest('[data-import-cancel="true"]')) {
            close();
            resolve(null);
            return;
          }
          if (target.closest('[data-import-confirm="true"]')) {
            const value = textarea?.value ?? '';
            close();
            resolve(value);
          }
        };

        const onKeydown = (event: KeyboardEvent) => {
          if (event.key !== 'Escape') return;
          document.removeEventListener('keydown', onKeydown);
          close();
          resolve(null);
        };

        overlay.addEventListener('click', onClick, { once: false });
        document.addEventListener('keydown', onKeydown, { once: true });
        window.setTimeout(() => textarea?.focus(), 0);
      });

    const buildPayload = () => {
      const categoryId = Number(category.value);
      if (!categoryId) {
        throw new Error('Category is required.');
      }
      const parsedBlocks = contentBlocksEditor.getBlocks();

      return {
        title: title.value.trim(),
        slug: slug.value.trim() || undefined,
        category: categoryId,
        excerpt: excerpt.value.trim(),
        description: description.value.trim(),
        content: content.value.trim(),
        content_format: parsedBlocks?.length ? ('blocks_v1' as const) : undefined,
        content_blocks: parsedBlocks || undefined,
        tag_ids: Array.from(tags.selectedOptions).map((option) => Number(option.value)).filter((value) => Number.isFinite(value)),
        external_url: externalUrl.value.trim(),
        source_name: sourceName.value.trim(),
        source_url: sourceUrl.value.trim(),
        is_featured: featured.checked,
        is_pinned: pinned.checked,
        status: status.value as NewsStatus,
        review_comment: reviewComment.value.trim(),
        seo_title: seoTitle.value.trim(),
        seo_description: seoDescription.value.trim(),
      };
    };

    const submit = async (mode: 'draft' | 'save' | 'publish') => {
      setLoading(true);
      try {
        const payload = buildPayload();
        if (mode === 'draft') {
          payload.status = 'draft';
        }

        const entity = isEdit ? await patchNews(editId, payload) : await createNews(payload);
        currentNewsId = entity.id;

        if (selectedCover) {
          await setNewsCover(entity.id, selectedCover);
        }

        if (selectedGallery.length) {
          for (let i = 0; i < selectedGallery.length; i += 1) {
            await uploadNewsMedia(entity.id, {
              file: selectedGallery[i].file,
              title: selectedGallery[i].file.name,
              sort_order: i,
            });
          }
        }

        if (mode === 'publish') {
          await publishNews(entity.id);
          currentStatus = 'published';
          status.value = 'published';
        } else {
          currentStatus = (payload.status || currentStatus) as NewsStatus;
        }

        lastSaved.textContent = `Last saved at ${new Date().toLocaleTimeString()}`;
        showAlert('success', mode === 'publish' ? 'News published successfully.' : 'News saved successfully.');
        updatePreview();

        window.setTimeout(() => {
          if (!disposed) navigate('/news');
        }, 450);
      } catch (error) {
        const fallback = error instanceof Error ? error.message : 'Failed to save news.';
        showAlert('error', getApiErrorMessage(error, fallback));
      } finally {
        if (!disposed) setLoading(false);
      }
    };

    const onImportJson = async () => {
      const raw = await openJsonImportDialog();
      if (raw === null) return;
      if (!raw.trim()) {
        showAlert('error', 'JSON payload is empty.');
        return;
      }

      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(raw) as Record<string, unknown>;
      } catch {
        showAlert('error', 'Invalid JSON format.');
        return;
      }

      setImportLoading(true);
      try {
        if (currentNewsId) {
          const updated = await importNewsIntoExistingFromJson(currentNewsId, parsed);
          showAlert('success', `News updated from JSON: #${updated.id}.`);
          window.setTimeout(() => {
            if (!disposed) navigate(`/news/create?id=${updated.id}`, { replace: true });
          }, 250);
        } else {
          const created = await importNewsFromJson(parsed);
          showAlert('success', `News imported: #${created.id}. Opening editor...`);
          window.setTimeout(() => {
            if (!disposed) navigate(`/news/create?id=${created.id}`);
          }, 250);
        }
      } catch (error) {
        showAlert('error', getApiErrorMessage(error, 'Failed to import JSON news.'));
      } finally {
        if (!disposed) setImportLoading(false);
      }
    };
    const onImportJsonClick = () => {
      void onImportJson();
    };

    const init = async () => {
      const [categories, newsTags] = await Promise.all([listNewsCategories(), listNewsTags()]);
      availableContentTags = newsTags.map((item) => ({ id: item.id, name: item.name, slug: item.slug }));
      categories.forEach((item) => category.insertAdjacentHTML('beforeend', `<option value="${item.id}">${item.name}</option>`));
      newsTags.forEach((item) => tags.insertAdjacentHTML('beforeend', `<option value="${item.id}">${item.name}</option>`));

        if (isEdit) {
          const item = await getNews(editId);
          if (disposed) return;
          currentNewsId = item.id;

        breadcrumb.textContent = 'Edit News Post';
        formTitle.textContent = 'Edit News Post';
        modeBadge.textContent = ` ${item.status}`;

        title.value = item.title || '';
        slug.value = item.slug || '';
        excerpt.value = item.excerpt || '';
        description.value = item.description || '';
        content.value = item.content || '';
        contentBlocksEditor.setBlocks(item.content_blocks);
        category.value = String(item.category || '');
        externalUrl.value = item.external_url || '';
        sourceName.value = item.source_name || '';
        sourceUrl.value = item.source_url || '';
        featured.checked = item.is_featured;
        pinned.checked = item.is_pinned;
        status.value = item.status;
        reviewComment.value = item.review_comment || '';
        seoTitle.value = item.seo_title || '';
        seoDescription.value = item.seo_description || '';
        currentStatus = item.status;

        item.tag_ids.forEach((tagId) => {
          const option = tags.querySelector<HTMLOptionElement>(`option[value="${tagId}"]`);
          if (option) option.selected = true;
        });

        if (item.cover_image) {
          coverPreview.innerHTML = `<img class="w-full rounded-lg border border-slate-200 max-h-52 object-cover" src="${item.cover_image}" alt="Cover"/>`;
        }

        const mediaPayload = await listAllNewsMedia(editId);
        if (disposed) return;
        existingGallery = mediaPayload.results
          .filter((entry) => !isContentBlockMediaTitle(entry.title))
          .sort((a, b) => a.sort_order - b.sort_order || a.id - b.id)
          .map((entry) => ({ id: entry.id, url: entry.file_url, title: entry.title || '' }));
        renderGallery();
      }

      updatePreview();
    };

    title.addEventListener('input', onTitleInput);
    slug.addEventListener('input', onSlugInput);
    excerpt.addEventListener('input', updatePreview);
    seoDescription.addEventListener('input', updatePreview);
    status.addEventListener('change', updatePreview);
    coverFile.addEventListener('change', onCoverChange);
    galleryFiles.addEventListener('change', onGalleryChange);
    galleryPreview.addEventListener('click', onGalleryClick);

    saveDraft.addEventListener('click', () => void submit('draft'));
    saveChanges.addEventListener('click', () => void submit('save'));
    publish.addEventListener('click', () => void submit('publish'));
    importJson.addEventListener('click', onImportJsonClick);
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      void submit('save');
    });

    renderGallery();
    void init().catch((error) => {
      showAlert('error', getApiErrorMessage(error, 'Failed to load form dictionaries.'));
    });

    return () => {
      disposed = true;
      title.removeEventListener('input', onTitleInput);
      slug.removeEventListener('input', onSlugInput);
      excerpt.removeEventListener('input', updatePreview);
      seoDescription.removeEventListener('input', updatePreview);
      status.removeEventListener('change', updatePreview);
      coverFile.removeEventListener('change', onCoverChange);
      galleryFiles.removeEventListener('change', onGalleryChange);
      galleryPreview.removeEventListener('click', onGalleryClick);
      importJson.removeEventListener('click', onImportJsonClick);
      contentBlocksEditor.destroy();
      if (currentCoverObjectUrl) URL.revokeObjectURL(currentCoverObjectUrl);
      selectedGallery.forEach((item) => URL.revokeObjectURL(item.url));
    };
  }, [navigate, searchParams]);

  return (
    <>
      <Helmet>
        <title>MotoJourney | Create News</title>
      </Helmet>
      <div className="font-display bg-background-light text-slate-900 min-h-screen" ref={containerRef}>
        <div dangerouslySetInnerHTML={{ __html: template }} />
      </div>
    </>
  );
}
